// Hook backing the bell-icon notification center. Wraps the
// /notifications endpoints exposed by the backend's userNotificationsRouter
// and provides the list, unread badge count, mark-read mutation (used when
// a row is clicked through to its target), and delete mutations (the
// 'x' button and the Clear-all action).
//
// Freshness:
//   • 15s polling tick while the user is authenticated.
//   • Immediate refresh when the tab regains focus / becomes visible, so
//     a sender returning to the app after a recipient acted on a task
//     sees the new event right away instead of waiting for the next tick.
// Push notifications remain the primary real-time signal; the bell is for
// in-app catch-up.

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "./useAuth";

export interface BellNotification {
  id: string;
  userID: string;
  message: string;
  type: "task" | "invite" | "doc";
  event: string;
  link: string;
  read: boolean;
  createdAt: string;
  actorId?: string;
  taskId?: string;
  inviteId?: string;
  docFieldKey?: string;
}

interface UseNotificationsResult {
  notifications: BellNotification[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  clearAll: () => Promise<void>;
}

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const POLL_INTERVAL_MS = 15 * 1000;

export function useNotifications(): UseNotificationsResult {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<BellNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // Track an in-flight fetch so a refresh triggered by the user can't
  // double up with a polling tick that lands at the same time.
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/notifications`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(
        Array.isArray(data.notifications) ? data.notifications : [],
      );
      setUnreadCount(
        typeof data.unreadCount === "number" ? data.unreadCount : 0,
      );
    } catch (err) {
      // Silent — bell stays at its last-known state on transient errors.
      console.warn("[useNotifications] refresh failed:", err);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [isAuthenticated]);

  // Initial fetch + polling + focus/visibility-driven refresh. The focus
  // path is what makes "I just switched back to my tab" feel instant
  // without dropping the poll interval into single-digit seconds.
  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onFocus = () => refresh();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAuthenticated, refresh]);

  // Optimistic mark-read for the click-through path. Flip locally first
  // so the UI feels instant, then PATCH the server. On failure we
  // re-fetch rather than rolling back, since the user's intent was
  // clearly to read.
  const markRead = useCallback(
    async (id: string) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id && !n.read ? { ...n, read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      try {
        await fetch(`${BASE}/notifications/${encodeURIComponent(id)}/read`, {
          method: "PATCH",
          credentials: "include",
        });
      } catch (err) {
        console.warn("[useNotifications] markRead failed:", err);
        refresh();
      }
    },
    [refresh],
  );

  // Single-row delete (the 'x' button). Optimistically remove from the
  // visible list and adjust the unread badge before the server confirms.
  const dismiss = useCallback(
    async (id: string) => {
      let removedWasUnread = false;
      setNotifications((prev) => {
        const next: BellNotification[] = [];
        for (const n of prev) {
          if (n.id === id) {
            if (!n.read) removedWasUnread = true;
            continue;
          }
          next.push(n);
        }
        return next;
      });
      // Capture-then-decrement so the unread count drops only if the
      // removed row was actually unread.
      setUnreadCount((prev) => (removedWasUnread ? Math.max(0, prev - 1) : prev));
      try {
        await fetch(`${BASE}/notifications/${encodeURIComponent(id)}`, {
          method: "DELETE",
          credentials: "include",
        });
      } catch (err) {
        console.warn("[useNotifications] dismiss failed:", err);
        refresh();
      }
    },
    [refresh],
  );

  // Batch delete behind the Clear-all button. Optimistically empties the
  // list and zeroes the badge.
  const clearAll = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await fetch(`${BASE}/notifications`, {
        method: "DELETE",
        credentials: "include",
      });
    } catch (err) {
      console.warn("[useNotifications] clearAll failed:", err);
      refresh();
    }
  }, [refresh]);

  return {
    notifications,
    unreadCount,
    loading,
    refresh,
    markRead,
    dismiss,
    clearAll,
  };
}
